// PreventiApp · Sprint 3 · Etapa 5 — Panel de Carga Masiva (super_admin_inside)
// Flujo: Descargar plantilla → Subir archivo → Validar (preview) → Ejecutar → CSV respaldo
import React, { useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PreventiAppLogo from '../components/PreventiAppLogo';

const API = process.env.REACT_APP_BACKEND_URL;

const TIPOS = {
  colegios: {
    label: 'Colegios',
    descripcion: 'Alta y actualización idempotente por RBD.',
    plantilla: 'plantilla_colegios',
    columnasCriticas: ['rbd', 'nombre', 'estado'],
  },
  usuarios: {
    label: 'Usuarios',
    descripcion: 'Alta de cuentas con contraseña temporal (12 chars). El RBD del colegio debe existir previamente.',
    plantilla: 'plantilla_usuarios',
    columnasCriticas: ['colegio_rbd', 'nombre_completo', 'rut', 'correo_institucional', 'rol', 'estado'],
  },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

function csvFromString(str) {
  return new Blob(['\ufeff' + str], { type: 'text/csv;charset=utf-8' });
}

export default function AdminImport() {
  const { profile, session, signOut, loading } = useAuth();
  const [tipo, setTipo] = useState('colegios');
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [runResult, setRunResult] = useState(null);
  const inputRef = useRef(null);

  const cfg = TIPOS[tipo];

  const previewCols = useMemo(() => {
    if (!validation?.preview?.length) return [];
    return Object.keys(validation.preview[0]);
  }, [validation]);

  const errorRows = useMemo(() => {
    const map = new Map();
    (validation?.errores || []).forEach((e) => map.set(e.fila, e.errores));
    return map;
  }, [validation]);

  // Gate por rol (sólo super_admin_inside puede ver Etapa 5)
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#0B1B5E]" data-testid="admin-import-loading">Cargando…</div>;
  }
  if (!session) return <Navigate to="/login" replace />;
  if (profile && profile.rol !== 'super_admin_inside') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FE] px-4" data-testid="admin-import-forbidden">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <p className="text-[#0B1B5E] font-bold text-lg">Acceso restringido</p>
          <p className="text-sm text-[#475472] mt-2">Esta sección es exclusiva del equipo INSIDE (super_admin_inside).</p>
          <Link to="/admin" className="mt-4 inline-block text-[#5346E6] text-sm font-bold" data-testid="back-to-admin">← Volver al panel</Link>
        </div>
      </div>
    );
  }

  const resetState = () => {
    setFile(null); setValidation(null); setRunResult(null); setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const onChangeTipo = (t) => { setTipo(t); resetState(); };

  const descargarPlantilla = async (formato) => {
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/templates/${tipo}.${formato}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      downloadBlob(blob, `${cfg.plantilla}.${formato}`);
    } catch (e) {
      setError(`No se pudo descargar la plantilla: ${e.message}`);
    }
  };

  const onSelectFile = (e) => {
    const f = e.target.files?.[0];
    setValidation(null); setRunResult(null); setError('');
    if (!f) { setFile(null); return; }
    const name = f.name.toLowerCase();
    if (name.endsWith('.xls')) {
      setError('Formato .xls no soportado. Use .xlsx o .csv UTF-8.');
      setFile(null); if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (!name.endsWith('.xlsx') && !name.endsWith('.csv')) {
      setError('Sólo se aceptan archivos .xlsx o .csv UTF-8.');
      setFile(null); if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(f);
  };

  const validar = async () => {
    if (!file) return;
    setBusy(true); setError(''); setValidation(null); setRunResult(null);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      fd.append('file', file);
      const r = await fetch(`${API}/api/admin/import/validate`, { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setValidation(j);
    } catch (e) {
      setError(`Error validando archivo: ${e.message}`);
    } finally { setBusy(false); }
  };

  const ejecutar = async () => {
    if (!file || !validation?.ok) return;
    if (!window.confirm(`¿Confirmas ejecutar la carga de ${validation.total_filas} ${tipo}? Esta acción escribirá en la base.`)) return;
    setBusy(true); setError(''); setRunResult(null);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      fd.append('file', file);
      if (profile?.id) fd.append('actor_id', profile.id);
      const r = await fetch(`${API}/api/admin/import/run`, { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || j.detail || `HTTP ${r.status}`);
      setRunResult(j);
      // Descargar CSV de respaldo automáticamente
      if (j.csv_respaldo) {
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        downloadBlob(csvFromString(j.csv_respaldo), `respaldo_${tipo}_${stamp}.csv`);
      }
    } catch (e) {
      setError(`Error ejecutando importación: ${e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FE] py-6 px-4" data-testid="admin-import-panel">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <PreventiAppLogo />
            <div>
              <h1 className="text-xl font-extrabold text-[#0B1B5E]">Carga masiva</h1>
              <p className="text-xs text-[#475472]">Alta masiva de colegios y usuarios desde plantilla.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-xs font-bold text-[#5346E6] hover:underline" data-testid="link-admin-home">← Panel</Link>
            <button onClick={signOut} className="text-xs font-bold text-[#475472] hover:text-[#0B1B5E]" data-testid="admin-import-logout">Salir</button>
          </div>
        </div>

        {/* Selector de tipo */}
        <div className="bg-white rounded-2xl shadow-sm p-2 flex gap-2 mb-5">
          {Object.entries(TIPOS).map(([k, v]) => (
            <button
              key={k}
              onClick={() => onChangeTipo(k)}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition ${tipo === k ? 'bg-[#0B1B5E] text-white' : 'text-[#475472] hover:bg-[#F4F7FE]'}`}
              data-testid={`tab-${k}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Plantillas */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h2 className="text-base font-bold text-[#0B1B5E]">1 · Descarga la plantilla oficial</h2>
              <p className="text-sm text-[#475472] mt-1">{cfg.descripcion}</p>
              <p className="text-xs text-[#475472] mt-2">
                Columnas obligatorias: <span className="font-mono text-[#0B1B5E]">{cfg.columnasCriticas.join(', ')}</span>
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => descargarPlantilla('xlsx')}
                className="px-4 py-2.5 rounded-xl bg-[#0B1B5E] hover:bg-[#5346E6] text-white text-sm font-bold"
                data-testid={`download-${tipo}-xlsx`}
              >
                Descargar .xlsx
              </button>
              <button
                onClick={() => descargarPlantilla('csv')}
                className="px-4 py-2.5 rounded-xl border border-[#0B1B5E] text-[#0B1B5E] hover:bg-[#F4F7FE] text-sm font-bold"
                data-testid={`download-${tipo}-csv`}
              >
                Descargar .csv
              </button>
            </div>
          </div>
        </div>

        {/* Upload + Validate */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-5">
          <h2 className="text-base font-bold text-[#0B1B5E]">2 · Sube tu archivo y valida</h2>
          <p className="text-sm text-[#475472] mt-1">Acepta <span className="font-mono">.xlsx</span> o <span className="font-mono">.csv UTF-8</span>. La validación no escribe en la base.</p>

          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={onSelectFile}
              className="block text-sm text-[#0B1B5E] file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-[#F4F7FE] file:text-[#0B1B5E] file:font-bold hover:file:bg-[#DDE6F7]"
              data-testid="file-input"
            />
            <button
              onClick={validar}
              disabled={!file || busy}
              className="px-4 py-2.5 rounded-xl bg-[#5346E6] hover:bg-[#0B1B5E] text-white text-sm font-bold disabled:bg-[#9aa3b8] disabled:cursor-not-allowed"
              data-testid="btn-validate"
            >
              {busy ? 'Procesando…' : 'Validar archivo'}
            </button>
            {(file || validation || runResult) && (
              <button onClick={resetState} className="text-xs text-[#475472] hover:text-[#0B1B5E]" data-testid="btn-reset">Limpiar</button>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="error-banner">{error}</div>
          )}
        </div>

        {/* Resumen validación */}
        {validation && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-5" data-testid="validation-result">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-base font-bold text-[#0B1B5E]">Resultado de validación</h2>
              <div className="flex gap-2 text-xs">
                <span className="px-3 py-1 rounded-full bg-[#F4F7FE] text-[#0B1B5E] font-bold" data-testid="stat-total">Total: {validation.total_filas}</span>
                <span className="px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534] font-bold" data-testid="stat-ok">OK: {validation.total_filas - validation.errores.length}</span>
                <span className="px-3 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] font-bold" data-testid="stat-err">Errores: {validation.errores.length}</span>
              </div>
            </div>

            {/* Errores */}
            {validation.errores.length > 0 && (
              <div className="mt-4 max-h-56 overflow-y-auto border border-[#FEE2E2] rounded-xl divide-y divide-[#FEE2E2]" data-testid="error-list">
                {validation.errores.map((e) => (
                  <div key={e.fila} className="p-3 text-xs">
                    <span className="font-bold text-[#991B1B]">Fila {e.fila}:</span>{' '}
                    <span className="text-[#7F1D1D]">{e.errores.join(' · ')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Preview */}
            <div className="mt-4 overflow-x-auto max-h-80 border border-[#E5EAF5] rounded-xl">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="bg-[#F4F7FE] text-[#0B1B5E]">
                  <tr>
                    <th className="px-2 py-2 text-left font-bold">Fila</th>
                    {previewCols.map((c) => (
                      <th key={c} className="px-2 py-2 text-left font-bold whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validation.preview.map((row, idx) => {
                    const fila = idx + 2;
                    const hasErr = errorRows.has(fila);
                    return (
                      <tr key={fila} className={hasErr ? 'bg-[#FEF2F2]' : 'odd:bg-white even:bg-[#FAFBFE]'}>
                        <td className="px-2 py-1.5 font-mono text-[#475472]">{fila}</td>
                        {previewCols.map((c) => (
                          <td key={c} className="px-2 py-1.5 whitespace-nowrap text-[#0B1B5E]">{row[c]}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Ejecutar */}
            <div className="mt-5 flex items-center justify-end gap-3">
              <p className="text-xs text-[#475472]">
                {validation.ok
                  ? 'Sin errores. Puedes ejecutar la carga.'
                  : 'Corrige los errores y vuelve a validar para habilitar la ejecución.'}
              </p>
              <button
                onClick={ejecutar}
                disabled={!validation.ok || busy}
                className="px-5 py-2.5 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold disabled:bg-[#9aa3b8] disabled:cursor-not-allowed"
                data-testid="btn-run-import"
              >
                {busy ? 'Ejecutando…' : 'Ejecutar importación'}
              </button>
            </div>
          </div>
        )}

        {/* Resultado de ejecución */}
        {runResult && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-10" data-testid="run-result">
            <h2 className="text-base font-bold text-[#0B1B5E]">3 · Ejecución completada</h2>
            <div className="mt-3 flex gap-2 text-xs flex-wrap">
              <span className="px-3 py-1 rounded-full bg-[#F4F7FE] text-[#0B1B5E] font-bold">Carga ID: {String(runResult.carga_id).slice(0, 8)}…</span>
              <span className="px-3 py-1 rounded-full bg-[#F4F7FE] text-[#0B1B5E] font-bold">Total: {runResult.filas_total}</span>
              <span className="px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534] font-bold" data-testid="run-stat-ok">OK: {runResult.filas_ok}</span>
              <span className="px-3 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] font-bold" data-testid="run-stat-err">Errores: {runResult.filas_error}</span>
            </div>
            <p className="text-xs text-[#475472] mt-3">
              Se descargó automáticamente el CSV de respaldo con el detalle por fila
              {tipo === 'usuarios' && <span> (incluye contraseñas temporales — el envío automático por correo aún no está habilitado)</span>}.
            </p>
            {runResult.csv_respaldo && (
              <button
                onClick={() => {
                  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                  downloadBlob(csvFromString(runResult.csv_respaldo), `respaldo_${tipo}_${stamp}.csv`);
                }}
                className="mt-3 px-4 py-2 rounded-xl border border-[#0B1B5E] text-[#0B1B5E] text-xs font-bold hover:bg-[#F4F7FE]"
                data-testid="btn-download-backup"
              >
                Re-descargar CSV de respaldo
              </button>
            )}
            {runResult.errores?.length > 0 && (
              <div className="mt-4 max-h-56 overflow-y-auto border border-[#FEE2E2] rounded-xl divide-y divide-[#FEE2E2]">
                {runResult.errores.map((e, i) => (
                  <div key={i} className="p-3 text-xs">
                    <span className="font-bold text-[#991B1B]">Fila {e.fila}:</span>{' '}
                    <span className="text-[#7F1D1D]">{e.error || e.errores?.join(' · ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
